import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TruckIcon } from '@heroicons/react/24/outline';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput from '../../pro/components/orderform/SelectInput';
import Icon from '../../pro/components/orderform/Icon';
import Card from '../components/Card';
import AutoCalcRow from '../components/AutoCalcRow';
import TariffRatesCard from '../components/TariffRatesCard';
import type { DeliveryLaneRow, TariffRateRow, LocationLookupRow } from '../fixtures';
import type { LaneFieldsPayload, RateStandards, StopoffFieldsPayload, ChargeCatalogRow } from '../../../services/quote.service';
import StopoffsEditor from '../components/StopoffsEditor';
import LocationLookupField from '../components/LocationLookupField';
import useQuoteDeliveryGeo from '../components/useQuoteDeliveryGeo';
import useQuoteFsc, { fscRateUnit } from '../components/useQuoteFsc';
import { deliveryGuard, belowMinimum, mileageRateFor } from '../rateGuards';
import { WAREHOUSES, LANE_EQUIP_TYPES, FSC_TYPES, RATE_TYPE_QUALIFIERS } from '../quoteOptions';

const LBL = 110;

const COLUMNS = ['Warehouse', 'Delivery Type', 'Equip Type', 'Name', 'Address', 'City', 'State', 'Zip', 'Carrier', 'DTD', 'Rate'];

/** camelCase DeliveryLaneRow key → deliveryquote del* column. */
const LANE_MAP: Partial<Record<keyof DeliveryLaneRow, keyof LaneFieldsPayload>> = {
  warehouse: 'delwarehouse', rateZone: 'delratezone', deliveryType: 'deldeliverytype',
  equipType: 'delequiptype', deliverToName: 'deldelivertoname', deliverToCode: 'deldelivertocode',
  addr1: 'deladdr1', addr2: 'deladdr2', city: 'delcity', state: 'delstate', zip: 'delzip',
  miles: 'delmiles', carrierCode: 'delcarriercode', carrierRate: 'delcarrierrate',
  carrierQuoteNumber: 'delcarrierquotenumber', deliveryRate: 'deldeliveryrate',
  fscRate: 'delfscrate', fscType: 'delfsctype', fscTariff: 'delfsctariff',
  allFscRate: 'delallfscrate', inDray: 'delindray',
  overloadRate: 'deloverloadrate', overloadType: 'deloverloadtype',
  waitingTimeFree: 'delwaitingtimefree', yardStorageFree: 'delyardstoragefree',
  chassisFeeType: 'delchassisfeetype', chassisFeeDays: 'delchassisfeedays',
  lightweightChassisFeeType: 'dellightweightchassisfeetype',
  lightweightChassisFeeDays: 'dellightweightchassisfeedays',
  comments: 'delcomments',
};
const LANE_NUMBER_FIELDS: ReadonlySet<keyof DeliveryLaneRow> = new Set([
  'deliverToCode',
  'miles', 'carrierRate', 'deliveryRate', 'fscRate', 'fscTariff', 'allFscRate',
  'overloadRate', 'waitingTimeFree', 'yardStorageFree', 'chassisFeeDays',
  'lightweightChassisFeeDays',
]);

/** Build the changed-fields PATCH payload for a lane draft vs its original. */
function buildLanePayload(orig: DeliveryLaneRow, draft: DeliveryLaneRow): LaneFieldsPayload {
  const out: LaneFieldsPayload = {};
  (Object.keys(LANE_MAP) as Array<keyof DeliveryLaneRow>).forEach((k) => {
    const a = orig[k] == null ? '' : String(orig[k]);
    const b = draft[k] == null ? '' : String(draft[k]);
    if (a !== b) {
      (out as Record<string, unknown>)[LANE_MAP[k]!] =
        LANE_NUMBER_FIELDS.has(k) ? Number(draft[k]) || 0 : b;
    }
  });
  return out;
}

interface Props {
  /** Parent warehouse quote rate zone, which supplies the mileage origin ZIP. */
  rateZone?: string;
  lanes: DeliveryLaneRow[];
  tariffRates?: TariffRateRow[];
  /** Lane CRUD pass-throughs. When provided the detail panel becomes a
   *  per-lane editor with Save/Delete, and "+ Add New" creates a lane. */
  lanesEditable?: boolean;
  onAddLane?: (payload: LaneFieldsPayload) => void | Promise<boolean | void>;
  onSaveLane?: (laneId: number, payload: LaneFieldsPayload) => void;
  onDeleteLane?: (laneId: number) => void;
  /** Stopoff CRUD pass-throughs (lane-scoped; edit/delete keyed by stopId). */
  onAddStopoff?: (laneId: number, payload: StopoffFieldsPayload) => void;
  onUpdateStopoff?: (stopId: number, payload: StopoffFieldsPayload) => void;
  onDeleteStopoff?: (stopId: number) => void;
  /** Per-lane additional-charge CRUD (deliveryquote-scoped, pinned to laneId).
   *  `chargesEditable` may be true while `lanesEditable` is false: a SECURED
   *  quote keeps its lanes locked but still accepts NEW charges (add-only, as
   *  on DirectTab) — Lisa's rule: no new revision needed to add an item. */
  chargesEditable?: boolean;
  chargesAddOnly?: boolean;
  chargeCatalog?: ChargeCatalogRow[];
  onAddLaneCharge?: (laneId: number, payload: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number }) => void;
  onChangeCharge?: (id: number, patch: { addlchargeratetype?: string; addlchargerate?: number }) => void;
  onDeleteCharge?: (id: number) => void;
  /** Standards lookups for the below-minimum delivery-rate warning. */
  rateStandards?: RateStandards | null;
}

/**
 * Delivery tab for WAREHOUSE quotes.
 *
 * Master/detail: top table of every delivery lane (deliveryquote rows for
 * this revision); clicking a row opens the bottom three-card panel with the
 * lane details, rates, and the lane's stopoffs. Tariff Rates below shows
 * delivery-scoped accessorial charges.
 */
const DeliveryTab: React.FC<Props> = ({
  lanes, tariffRates = [], lanesEditable, onAddLane, onSaveLane, onDeleteLane,
  onAddStopoff, onUpdateStopoff, onDeleteStopoff,
  chargesEditable, chargesAddOnly = false,
  chargeCatalog = [], onAddLaneCharge, onChangeCharge, onDeleteCharge,
  rateStandards = null, rateZone = '',
}) => {
  const [adding, setAdding] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(lanes[0]?.laneId ?? null);
  // Per-lane edit draft (only meaningful when lanesEditable).
  const [draftLane, setDraftLane] = useState<DeliveryLaneRow | null>(null);

  // If lanes load asynchronously (or the user navigates between quotes),
  // make sure the selection always points at a real lane.
  useEffect(() => {
    if (lanes.length === 0) {
      setSelectedId(null);
    } else if (selectedId === null || !lanes.some((l) => l.laneId === selectedId)) {
      setSelectedId(lanes[0].laneId);
    }
  }, [lanes, selectedId]);

  const selected = useMemo(
    () => lanes.find((l) => l.laneId === selectedId) ?? lanes[0] ?? null,
    [lanes, selectedId],
  );

  // Re-seed the draft whenever the selected lane (or fresh server data) changes.
  useEffect(() => {
    setDraftLane(selected ? { ...selected } : null);
  }, [selected]);

  const editing = Boolean(lanesEditable && onSaveLane);
  const laneCh = editing
    ? (field: keyof DeliveryLaneRow) => (v: string) =>
        setDraftLane((d) => (d ? { ...d, [field]: v } : d))
    : () => undefined;
  const laneDirty = Boolean(
    editing && selected && draftLane && Object.keys(buildLanePayload(selected, draftLane)).length,
  );
  const saveLane = () => {
    if (!selected || !draftLane || !onSaveLane) return;
    const payload = buildLanePayload(selected, draftLane);
    if (Object.keys(payload).length) onSaveLane(selected.laneId, payload);
  };

  // P10 #22: bind a cms.locations row onto the lane's deliver-to (fills the
  // denormalized address + deldelivertocode; frozen by N18 until re-typed).
  const pickLaneLocation = (row: LocationLookupRow) =>
    setDraftLane((d) => (d ? {
      ...d,
      deliverToName: row.lname,
      addr1: row.address1,
      addr2: row.address2,
      city: row.city,
      state: row.state,
      zip: row.zip,
      deliverToCode: row.locationcode,
    } : d));

  // A secured quote may add a complete lane, but existing lanes stay locked.
  if (adding && onAddLane) return <NewLaneEditor
    onAdd={onAddLane} onDone={() => setAdding(false)} rateStandards={rateStandards}
    selectedIndex={lanes.length + 1} rateZone={rateZone} />;

  const addButton = onAddLane ? (
    <button
      type="button"
      onClick={() => { if (lanesEditable) void onAddLane({}); else setAdding(true); }}
      className="text-[11.5px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
    >
      + Add New
    </button>
  ) : null;

  if (lanes.length === 0) {
    return (
      <Card
        icon={<TruckIcon className="h-3.5 w-3.5" />}
        title="Delivery Lanes"
        right={addButton}
      >
        <div className="px-3 py-10 text-center text-slate-400 dark:text-slate-500 text-[12.5px] bg-slate-50 dark:bg-slate-900/40 border border-dashed border-slate-200 dark:border-slate-700 rounded-md">
          {onAddLane
            ? 'No delivery lanes on this revision yet. Click + Add New to create one.'
            : 'No delivery lanes on this revision yet.'}
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        icon={<TruckIcon className="h-3.5 w-3.5" />}
        title="Delivery Lanes"
        right={
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">{lanes.length} {lanes.length === 1 ? 'lane' : 'lanes'}</span>
            {addButton}
          </div>
        }
        flushBody
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12px] dark:text-slate-200">
            <thead>
              <tr>
                {COLUMNS.map((h, i) => (
                  <th
                    key={h}
                    className={[
                      'px-2.5 py-1.5 text-[10.5px] font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider',
                      'bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap',
                      i === COLUMNS.length - 1 ? 'text-right' : 'text-left',
                    ].join(' ')}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lanes.map((r) => {
                const isSel = r.laneId === selectedId;
                return (
                  <tr
                    key={r.laneId}
                    onClick={() => setSelectedId(r.laneId)}
                    className={`cursor-pointer ${isSel ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                  >
                    <td className={`px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700 ${isSel ? 'font-semibold text-blue-800 dark:text-blue-300' : 'text-slate-900 dark:text-slate-100'}`}>{r.warehouse || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700">{r.rateZone || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700">{r.equipType || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700 text-slate-400 dark:text-slate-500">{r.deliverToName || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700 text-slate-400 dark:text-slate-500">{r.addr1 || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700">{r.city || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700">{r.state || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700">{r.zip || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700 text-slate-400 dark:text-slate-500" title={r.carrierName && r.carrierCode ? `Carrier #${r.carrierCode}` : undefined}>{r.carrierName || r.carrierCode || '—'}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700">{r.inDray}</td>
                    <td className="px-2.5 py-1.5 border-b border-slate-100 dark:border-slate-700 text-right font-semibold">{r.deliveryRate || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && (
        <>
          {editing && (
            <div className="flex items-center justify-end gap-2">
              {laneDirty && <span className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold">Unsaved lane changes</span>}
              <button
                type="button"
                onClick={saveLane}
                disabled={!laneDirty}
                className="px-3 py-1.5 text-[11.5px] font-semibold text-white bg-blue-600 border border-blue-700 rounded disabled:opacity-40 hover:bg-blue-700"
              >
                Save Lane
              </button>
              {onDeleteLane && (
                <button
                  type="button"
                  onClick={() => onDeleteLane(selected.laneId)}
                  className="px-3 py-1.5 text-[11.5px] font-semibold text-red-600 dark:text-red-400 bg-white border border-red-300 rounded hover:bg-red-50 dark:bg-gray-800 dark:border-red-800 dark:hover:bg-red-900/20"
                >
                  Delete Lane
                </button>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <LaneDetailsCard key={selected.laneId} rateZone={rateZone}
              lane={editing && draftLane?.laneId === selected.laneId ? draftLane : selected}
              selectedIndex={lanes.findIndex((l) => l.laneId === selected.laneId) + 1}
              ch={laneCh}
              onPickLocation={editing ? pickLaneLocation : undefined}
            />
            <LaneRatesCard key={`rates-${selected.laneId}`} lane={editing && draftLane?.laneId === selected.laneId ? draftLane : selected} ch={laneCh} rateStandards={rateStandards} charges={tariffRates} />
            <StopoffsEditor
              stopoffs={selected.stopoffs}
              emptyLabel="No stopoffs on this lane"
              onAdd={lanesEditable && onAddStopoff ? (p) => onAddStopoff(selected.laneId, p) : undefined}
              onUpdate={lanesEditable ? onUpdateStopoff : undefined}
              onDelete={lanesEditable ? onDeleteStopoff : undefined}
            />
          </div>

          {/* Charges scoped to THIS lane (deliveryquote.tablerecno === laneId). */}
          <TariffRatesCard
            rows={tariffRates.filter((r) => r.tableRecno === selected.laneId)}
            caption={`charges for lane #${lanes.findIndex((l) => l.laneId === selected.laneId) + 1}`}
            editable={chargesEditable ?? lanesEditable}
            addOnly={chargesAddOnly}
            catalog={chargeCatalog}
            tablerecno={selected.laneId}
            onAdd={(chargesEditable ?? lanesEditable) && onAddLaneCharge ? (p) => onAddLaneCharge(selected.laneId, p) : undefined}
            onChangeRow={(chargesEditable ?? lanesEditable) ? onChangeCharge : undefined}
            onDeleteRow={(chargesEditable ?? lanesEditable) ? onDeleteCharge : undefined}
          />
        </>
      )}
    </>
  );
};

type LaneCh = (field: keyof DeliveryLaneRow) => ((v: string) => void) | undefined;

const EMPTY_LANE: DeliveryLaneRow = {
  laneId: 0, warehouse: '', deliveryType: '', equipType: '', deliverToName: '',
  addr1: '', addr2: '', city: '', state: '', zip: '', miles: '', carrierCode: '',
  carrierRate: '', carrierQuoteNumber: '', deliveryRate: '', drayWithCharges: '',
  fscRate: '', fscType: '', fscTariff: null, allFscRate: '', inDray: 'N',
  overloadRate: '', overloadType: '', waitingTimeFree: '', yardStorageFree: null,
  comments: '', stopoffs: [],
};

/** Collect fields before INSERT: a secured lane becomes read-only once saved. */
const NewLaneEditor: React.FC<{
  onAdd: NonNullable<Props['onAddLane']>; onDone: () => void;
  rateStandards: RateStandards | null; selectedIndex: number; rateZone: string;
}> = ({ onAdd, onDone, rateStandards, selectedIndex, rateZone }) => {
  const [lane, setLane] = useState<DeliveryLaneRow>(EMPTY_LANE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ch: LaneCh = (field) => (value) => setLane((d) => ({ ...d, [field]: value }));
  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const saved = await onAdd({ ...buildLanePayload(EMPTY_LANE, lane), delindray: lane.inDray });
      if (saved !== false) onDone();
    } catch {
      setError('Unable to add the delivery lane. Your entries have been kept; please retry.');
    } finally { setBusy(false); }
  };
  return <>
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-500">This new lane is editable until you click Add Lane. Existing lanes remain locked.</span>
      <button type="button" disabled={busy} onClick={onDone} className="text-sm text-slate-500">Cancel</button>
      <button type="button" disabled={busy} onClick={() => { void submit(); }} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">{busy ? 'Adding…' : 'Add Lane'}</button>
    </div>
    {error && <div role="alert" className="text-sm text-red-600">{error}</div>}
    <fieldset disabled={busy} className="grid grid-cols-3 gap-3">
      <LaneDetailsCard lane={lane} selectedIndex={selectedIndex} ch={ch} rateZone={rateZone}
        onPickLocation={(row) => setLane((d) => ({ ...d, deliverToName: row.lname,
          deliverToCode: row.locationcode, addr1: row.address1, addr2: row.address2,
          city: row.city, state: row.state, zip: row.zip }))} />
      <LaneRatesCard lane={lane} ch={ch} rateStandards={rateStandards} />
    </fieldset>
  </>;
};

const LaneDetailsCard: React.FC<{ lane: DeliveryLaneRow; selectedIndex: number; ch: LaneCh; rateZone: string; onPickLocation?: (row: LocationLookupRow) => void }> = ({ lane, selectedIndex, ch, onPickLocation, rateZone }) => {
  // A lookup may finish after the user selects another lane. Do not apply its
  // result to the newly selected draft in the parent.
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const geo = useQuoteDeliveryGeo({
    ratezone: rateZone, deliveryzone: lane.rateZone ?? '',
    zip: lane.zip, city: lane.city, state: lane.state, miles: lane.miles,
    enabled: Boolean(onPickLocation), lockAddress: (lane.deliverToCode ?? 0) > 0,
    onCityState: (city, state) => { if (active.current) { ch('city')?.(city); ch('state')?.(state); } },
    onZip: (zip) => { if (active.current) ch('zip')?.(zip); },
    onMiles: (miles) => { if (active.current) ch('miles')?.(miles); },
  });
  return (
  <Card
    icon={<Icon.ship className="h-3.5 w-3.5" />}
    title={`Lane #${selectedIndex}`}
    right={
      <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-300 px-2 py-px rounded tracking-wider dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800">
        EDITING
      </span>
    }
  >
    <Row><Label width={LBL}>Warehouse</Label><SelectInput value={lane.warehouse} onChange={ch('warehouse')} options={dedupOpts(lane.warehouse, WAREHOUSES)} /></Row>
    {/* V1 labels delratezone "Delivery Type" (defs_quote.pl:353, LOCAL/OTR).
        The deldeliverytype column was retired there (line 341 commented out)
        and is blank on every recent lane, so it gets no row here. */}
    <Row><Label width={LBL}>Delivery Type</Label><SelectInput value={lane.rateZone ?? ''} onChange={ch('rateZone')} options={dedupOpts(lane.rateZone ?? '', ['', 'LOCAL', 'OTR'])} ariaLabel="Lane Delivery Type" placeholder="—" /></Row>
    <Row><Label width={LBL}>Equip Type</Label><SelectInput value={lane.equipType} onChange={ch('equipType')} options={dedupOpts(lane.equipType, LANE_EQUIP_TYPES)} /></Row>
    {/* N18: freeze the denormalized deliver-to address once a cms.locations
        code is bound (deldelivertocode > 0); name stays editable for re-pick. */}
    <Row>
      <Label width={LBL}>Delivery Location</Label>
      {onPickLocation ? (
        <LocationLookupField
          value={lane.deliverToName}
          linkedCode={lane.deliverToCode ?? 0}
          onType={(v) => { ch('deliverToName')?.(v); ch('deliverToCode')?.('0'); }}
          onPick={onPickLocation}
          ariaLabel="Lane Delivery Location"
          placeholder="—"
        />
      ) : (
        <TextInput value={lane.deliverToName} onChange={ch('deliverToName')} ariaLabel="Lane Delivery Location" placeholder="—" />
      )}
    </Row>
    <Row><Label width={LBL}>Address 1</Label><TextInput value={lane.addr1} onChange={ch('addr1')} readOnly={(lane.deliverToCode ?? 0) > 0} ariaLabel="Lane Address 1" placeholder="—" /></Row>
    <Row><Label width={LBL}>Address 2</Label><TextInput value={lane.addr2} onChange={ch('addr2')} readOnly={(lane.deliverToCode ?? 0) > 0} ariaLabel="Lane Address 2" placeholder="—" /></Row>
    <Row><Label width={LBL}>City</Label><TextInput value={lane.city} onChange={ch('city')} onBlur={geo.handleCityStateBlur} readOnly={(lane.deliverToCode ?? 0) > 0} ariaLabel="Lane City" /></Row>
    <Row>
      <Label width={LBL}>State / Zip</Label>
      <TextInput value={lane.state} onChange={ch('state')} onBlur={geo.handleCityStateBlur} readOnly={(lane.deliverToCode ?? 0) > 0} ariaLabel="Lane State" width={48} />
      <TextInput value={lane.zip} onChange={ch('zip')} onBlur={geo.handleZipBlur} readOnly={(lane.deliverToCode ?? 0) > 0} ariaLabel="Lane Zip" width={96} />
    </Row>
    <Row>
      <Label width={LBL}>Miles</Label>
      <TextInput ariaLabel="Lane Miles" value={lane.miles} onChange={ch('miles')} width={96} placeholder="—" />
    </Row>
    {geo.geoBusy && <div role="status" className="text-xs text-slate-500">Calculating location and miles…</div>}
    {geo.milesError && <div role="status" className="text-xs text-amber-600">{geo.milesError}</div>}
    <Row>
      <Label width={LBL}>Carrier</Label>
      <TextInput value={lane.carrierCode} onChange={ch('carrierCode')} placeholder="—" />
      {lane.carrierName && <span className="text-[11px] ml-2 text-slate-500 dark:text-slate-400">{lane.carrierName}</span>}
    </Row>
    <Row>
      <Label width={LBL}>Carrier Rate / Quote #</Label>
      <TextInput value={lane.carrierRate} onChange={ch('carrierRate')} width={80} />
      <TextInput value={lane.carrierQuoteNumber} onChange={ch('carrierQuoteNumber')} placeholder="—" />
    </Row>
    <Divider />
    <Row><Label width={LBL}>Dray/Tran/Deliv</Label><SelectInput value={lane.inDray} onChange={ch('inDray')} options={['N', 'Y']} width={56} /></Row>
    {/* Chassis Fee / LW Chassis Fee are DIRECT-only (see DirectTab); the
        deliveryquote FLAT defaults were surfacing them on every lane. */}
  </Card>
);
};

const LaneRatesCard: React.FC<{ lane: DeliveryLaneRow; ch: LaneCh; rateStandards?: RateStandards | null; charges?: TariffRateRow[] }> = ({ lane, ch, rateStandards = null, charges = [] }) => {
  const totalWithCharges = charges
    .filter((c) => c.scope === 'deliveryquote' && c.tableRecno === lane.laneId && c.rateType === 'IN RATE')
    .reduce((total, c) => total + (Number(c.rate) || 0), Number(lane.deliveryRate) || 0)
    .toFixed(5);
  // Mileage-based unit rate (per the mockup hint "13.94 / mile"). Empty
  // when miles or rate is missing.
  const ratePerMile = useMemo(() => {
    const miles = parseFloat(lane.miles);
    const rate = parseFloat(lane.deliveryRate);
    if (!miles || !rate) return '';
    return `${(rate / miles).toFixed(2)} / mile`;
  }, [lane.miles, lane.deliveryRate]);

  const changeFscRate = ch('fscRate');
  const changeFscType = ch('fscType');
  const fsc = useQuoteFsc({
    fsctype: lane.fscType, fsctariff: String(lane.fscTariff ?? ''),
    deliveryzone: lane.rateZone ?? '', fscrate: lane.fscRate,
    dray: totalWithCharges, miles: lane.miles,
    enabled: Boolean(changeFscRate), resolveReadOnly: true,
    onFscRate: (rate) => changeFscRate?.(rate),
  });
  const fscType = (lane.fscType || '').trim().toUpperCase();
  const dynamicFsc = fscType === 'CURRENT' || fscType === 'CUSTOMER';
  const displayedFsc = dynamicFsc ? fsc.resolvedRate ?? lane.fscRate : lane.fscRate;
  const fscUnit = fscRateUnit(lane.fscType, fsc.ratetype);

  // Below-minimum delivery-rate guard (V1 checkfield_transloaddelivrate +
  // deliverystandards: zip for LOCAL, city/state for OTR).
  const guard = deliveryGuard(rateStandards, lane.rateZone ?? '', lane.zip, lane.city, lane.state);
  const delivBelowMin = belowMinimum(guard, lane.deliveryRate);

  // #13: advisory ratezonemileage suggestion for this lane's zone + miles.
  const mileageSuggest = mileageRateFor(rateStandards?.mileage, lane.rateZone ?? '', lane.miles);

  return (
    <Card icon={<Icon.ticket className="h-3.5 w-3.5" />} title="Rates">
      <Row>
        <Label width={LBL}>Deliv Rate</Label>
        <TextInput ariaLabel="Lane Delivery Rate" value={lane.deliveryRate} onChange={ch('deliveryRate')} width={120} />
        {ratePerMile && <span className="text-[11px] text-slate-400 dark:text-slate-500">{ratePerMile}</span>}
        {guard && (
          <span className={`text-[10.5px] whitespace-nowrap ${delivBelowMin ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-400 dark:text-slate-500'}`}>
            {delivBelowMin ? `below min $${guard.minimumRate}` : `std $${guard.standardRate} / min $${guard.minimumRate}`}
          </span>
        )}
        {mileageSuggest && (
          <span className="text-[10.5px] text-blue-500 dark:text-blue-400 whitespace-nowrap">mileage ${mileageSuggest.mileageRate} {mileageSuggest.rateType}</span>
        )}
      </Row>
      <AutoCalcRow label="Total With Charges" value={totalWithCharges} labelWidth={LBL} />
      <Row>
        <Label width={LBL}>Deliv FSC</Label>
        <TextInput ariaLabel="Delivery FSC Rate" value={displayedFsc} onChange={changeFscRate} readOnly={fsc.fscReadOnly} width={80} placeholder="—" />
        {fscUnit && <span className="text-xs text-slate-500">{fscUnit}</span>}
        <SelectInput ariaLabel="Delivery FSC Type" value={lane.fscType} disabled={!changeFscType}
          onChange={changeFscType ? (value) => { changeFscType(value); changeFscRate?.(''); } : undefined} options={dedupOpts(lane.fscType, ['', ...FSC_TYPES])} placeholder="—" />
      </Row>
      <Row>
        <Label width={LBL}>Cust Deliv Tariff #</Label>
        <TextInput value={lane.fscTariff != null ? String(lane.fscTariff) : ''} placeholder="—" />
      </Row>
      <Row>
        <Label width={LBL}>Overflow Delivery</Label>
        <TextInput value={lane.overloadRate} onChange={ch('overloadRate')} width={80} />
        <SelectInput value={lane.overloadType} onChange={ch('overloadType')} options={dedupOpts(lane.overloadType, ['', ...RATE_TYPE_QUALIFIERS])} placeholder="—" />
      </Row>
      <Row>
        <Label width={LBL}>All In FSC</Label>
        <TextInput value={lane.allFscRate} onChange={ch('allFscRate')} width={80} placeholder="—" />
      </Row>
    </Card>
  );
};

const Divider: React.FC = () => <div className="h-px bg-slate-100 dark:bg-slate-700 my-2" />;

// Keep the current API value in the option list even if it's something the
// static list doesn't include (preserves order, removes dupes/empties).
function dedupOpts(current: string, rest: string[]) {
  return [current, ...rest].filter((v, i, a) => v && a.indexOf(v) === i);
}

export default DeliveryTab;
